import { SFPeopleOption } from 'sfui';
import { Pagination } from './Pagination';
import {
  AgencyTagValue,
  GeoLocationCoords,
  LocationAddressType,
  User,
  UserGroup,
  UserTitle,
  UserTitleBase
} from 'ui-smartforce-settings';
import { CADRMSReportValueType } from './CadReportValue';

export type ReportCreatedByUser = Pick<
  User,
  'id' | 'name' | 'race_ethnicity' | 'dob' | 'gender' | 'groups' | 'title'
>;

export type DateRange = {
  dateFrom?: string;
  dateTo?: string;
  exactDate?: boolean;
};

export type NumberRange = {
  from?: number;
  to?: number;
};

export type FieldValueType =
  | string
  | number
  | GeoLocationCoords
  | boolean
  | string[]
  | ItemModelValueType[]
  | number[]
  | LocationFormValueType
  | PeoplePickerValueType
  | moment.Moment
  | undefined
  | UserGroup[]
  | null
  | DateRange
  | LocationAreaType[]
  | NumberRange
  | ReportTag[];

export interface AuthorValueType {
  user_id: string;
  name: string;
  officer_id: string;
  role: string;
  email: string;
  avatar_thumbnail_url?: string | null;
  title: UserTitleBase;
}

export interface VersionValueType {
  v: number;
  author: AuthorValueType;
  created_at: string;
  from_public_api?: boolean;
}

export interface SectionValueType {
  [key: string]: FieldValueType;
}

export interface StateRelatedValueType {
  [key: string]: SectionValueType;
}

export interface DeviceValueType {
  coords: GeoLocationCoords | undefined;
  date: string;
  duration: number;
}

export interface ItemModelValueType {
  [key: string]: FieldValueType;
}

export interface CreatorUserType extends ItemModelValueType {
  groups?: UserGroup[];
}

export interface GeneralInformationValueType {
  [key: string]:
    | FieldValueType
    | StateRelatedValueType
    | DeviceValueType
    | LocationFormValueType;
  type: string;
  state_related: StateRelatedValueType;
  device?: DeviceValueType;
  location: LocationFormValueType;
  created_by_user?: ReportCreatedByUser;
}

export interface ItemValueType {
  [key: string]: SectionValueType;
}

export interface UseOfForceValueType {
  [key: string]: FieldValueType | ItemValueType[];
  other_agencies_involved: ItemValueType[];
}

export interface LocationAreaType {
  id: string;
  name: string;
}

export interface LocationTypeValue {
  [key: string]: FieldValueType;
}

export interface LocationFormValueType {
  // This doesn't work for BE team because they need the type_value field
  address: LocationAddressType;
  areas?: LocationAreaType[];
  details?: string;
  type: string;
  type_value?: LocationTypeValue;
}

export interface PeoplePickerUserType extends Omit<
  User,
  'agency_name' | 'is_active' | 'password' | 'height'
> {
  id: string;
  age?: number;
  height_ft?: number;
  height_in?: number;
  title?: UserTitle;
  years_of_service?: number;
}

export interface PeoplePickerValueType extends SFPeopleOption {
  asyncObject?: PeoplePickerUserType;
}

export interface ReportTag extends AgencyTagValue {
  id: string;
}

export interface ReportTags {
  agency: ReportTag[];
}

export interface ReportTagsHistory {
  old_tags: {
    agency: ReportTag[];
  };
  author: AuthorValueType;
  updated_at?: string;
}

export interface ReportableStatusType {
  is_reportable: boolean;
  author: AuthorValueType;
  updated_at: string;
}

export interface ReportValue {
  id?: number | string;
  agency_id?: string;
  user?: string; // This prop it's used only to save reports
  created_at?: string;
  created_in_version?: string;
  status?: ReportStatusValue;
  reportable?: ReportableStatusType[];
  general_information: GeneralInformationValueType;
  citizens: ItemValueType[];
  use_of_force?: UseOfForceValueType;
  complaints?: ItemValueType[];
  investigations?: ItemValueType[];
  officers?: ItemValueType[];
  tags?: ReportTags | null;
  tags_history?: ReportTagsHistory[] | null;
  versions?: VersionValueType[];
  public_api_version?: string;
  integration?: string;
  administrative?: ItemValueType[];
  cad_rms?: CADRMSReportValueType;
}

export interface ReportResponse extends Pagination {
  reports: ReportValue[];
}

export interface AgencyReportsPagination {
  url: string;
  body: any;
}

export interface AgencyReportsResponse {
  reports: ReportValue[];
  links: { next: AgencyReportsPagination };
  total: number;
}

export type ReportsTotals = Record<string, number>;

export type FiltersSubSectionValueType = Record<string, FieldValueType>;
export type FiltersSectionValueType = Record<
  string,
  FiltersSubSectionValueType | FieldValueType
>;

export type FiltersCategoryValueType = Record<
  string,
  FiltersSectionValueType | FieldValueType
>;
export type ReportListFilters = Record<string, FiltersCategoryValueType>;

export type ReportPreFilter =
  | 'all'
  | 'cad_rms'
  | 'contacts_reportable'
  | 'contacts_completed'
  | 'contacts_missing_information';

export const PRE_FILTERS_VALUES = [
  'all',
  'cad_rms',
  'contacts_reportable',
  'contacts_completed',
  'contacts_missing_information'
];

export type ReportStatusValue =
  | 'Archived'
  | 'Completed'
  | 'Missing Information';
